import { memo, useCallback, useMemo } from "react";

import type { ForNodeType } from "@/components/features/_model/types";
import * as Shared from "@/components/features/cancellation/ui/_shared";

import { Button } from "@/components/ui/button/button";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { CheckboxGroupField } from "@/components/ui/form/fields/check-box-field/check-box-group";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import { TextAreaField } from "@/components/ui/form/fields/text-area-field";
import { Label } from "@/components/ui/label/label";

import * as Constants from "./constants";

import type { CheckedState } from "@radix-ui/react-checkbox";

type Props = {
	dtEndBasic?: string;
	reason: ForNodeType<string>;
	comment?: ForNodeType<string | undefined | null>;
	alternativeSystemProductName?: ForNodeType<string | undefined | null>;
	alternativeSystemReason?: ForNodeType<string | undefined | null>;
	disabled: boolean;

	onChangeReasonOfChurnInfo: (checked: CheckedState, item: FormOptionType) => void;
	onChangeCommentOfChurnInfo: (value: string) => void;
	onChangeAlternativeSystemProductName: (value: string) => void;
	onChangeAlternativeSystemReason: (value: string) => void;
	onClickCancellation: () => Promise<void>;
};

export const CancellationMainQuestionForm = memo(
	({
		dtEndBasic,
		reason,
		comment,
		alternativeSystemProductName,
		alternativeSystemReason,
		disabled,

		onChangeCommentOfChurnInfo,
		onChangeReasonOfChurnInfo,
		onChangeAlternativeSystemProductName,
		onChangeAlternativeSystemReason,
		onClickCancellation,
	}: Props) => {
		const handleChangeCommentOfChurnInfo = useCallback(
			(value: string) => onChangeCommentOfChurnInfo(value),
			[onChangeCommentOfChurnInfo],
		);

		const handleChangeReasonOfChurnInfo = useCallback(
			(checked: CheckedState, item: FormOptionType) => onChangeReasonOfChurnInfo(checked, item),
			[onChangeReasonOfChurnInfo],
		);

		const handleChangeAlternativeSystemProductName = useCallback(
			(value: string) => onChangeAlternativeSystemProductName(value),
			[onChangeAlternativeSystemProductName],
		);

		const handleChangeAlternativeSystemReason = useCallback(
			(value: string) => onChangeAlternativeSystemReason(value),
			[onChangeAlternativeSystemReason],
		);

		const handleClickCancellation = useCallback(async () => {
			await onClickCancellation();
		}, [onClickCancellation]);

		const convertedArrayReason = useMemo(
			() => Shared.Converter.fromReasonToArrayConverter(reason.value),
			[reason.value],
		);

		const showAlternativeSystemFields = useMemo(
			() => convertedArrayReason.includes("他の予約システムへ乗り換え"),
			[convertedArrayReason],
		);

		return (
			<div>
				<div>
					<div>
						<p className="text-lg/[1rem]">CHILLNNをご利用いただき、ありがとうございました。</p>
						<p className="mt-2 text-lg/[1rem]">
							差し支えなければ、下記の解約にあたってのアンケートにご協力いただけますと幸いでございます。
						</p>
					</div>

					<div className="mt-4 space-y-1">
						{dtEndBasic && (
							<p className="text-sm text-[#4c4c4c]">
								※解約手続き後、現在のプランの有効期限 {dtEndBasic}{" "}
								まで管理画面は引き続きご利用いただけます。
							</p>
						)}
						<p className="text-sm text-[#4c4c4c]">
							※すべてのプランと部屋を編集画面より非公開にしてください。
						</p>
						<p className="text-sm text-[#4c4c4c]">
							※検索に引っかからないようページへのアクセスを削除したい場合、弊社へご一報ください。
						</p>
					</div>
				</div>

				<section className="mt-8">
					<div className="space-y-10">
						<div className="space-y-2">
							<p className="select-none text-sm font-semibold">解約理由（複数選択可）</p>
							<CheckboxGroupField
								value={convertedArrayReason}
								options={Constants.ChurnOptions}
								gridCols={2}
								errorMessage={reason.error}
								handleCheckedChange={handleChangeReasonOfChurnInfo}
							/>
						</div>

						{showAlternativeSystemFields && (
							<div className="space-y-2">
								<p className="text-gray-700 select-none text-sm font-semibold">
									乗り換え先の予約システムについて
								</p>

								<div className="space-y-4">
									<div className="space-y-2">
										<div className="flex items-center gap-x-2">
											<p className="select-none text-sm font-semibold">製品名</p>
											<Label text={"必須"} size={"sm"} variant="required" />
										</div>
										<InputTextField
											value={alternativeSystemProductName?.value || undefined}
											handleChange={handleChangeAlternativeSystemProductName}
											errorMessage={alternativeSystemProductName?.error}
											placeholder="例: 〇〇予約システム"
										/>
									</div>
									<div className="space-y-2">
										<div className="flex items-center gap-x-2">
											<p className="select-none text-sm font-semibold">乗り換える理由</p>
											<Label text={"必須"} size={"sm"} variant="required" />
										</div>
										<TextAreaField
											value={alternativeSystemReason?.value || undefined}
											handleChange={handleChangeAlternativeSystemReason}
											errorMessage={alternativeSystemReason?.error}
											placeholder="乗り換えを決めた理由をお聞かせください"
										/>
									</div>
								</div>
							</div>
						)}

						<div className="space-y-2">
							<p className="select-none text-sm font-semibold">
								ご質問やご要望がございましたらお気軽にお申し付けください
							</p>
							<TextAreaField
								value={comment?.value || undefined}
								handleChange={handleChangeCommentOfChurnInfo}
								errorMessage={comment?.error}
							/>
						</div>

						<Button
							type="button"
							variant={disabled ? "disabled" : "default"}
							onClick={handleClickCancellation}
						>
							解約する
						</Button>
					</div>
				</section>
			</div>
		);
	},
);
